import { inject, Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, map, mapTo, tap } from 'rxjs/operators';

import { FacilitiesResponse, Facility } from '@digital-platform/rs-cms/domain';
import { CmsContentService } from '../cms-content/cms-content.service';

@Injectable({
  providedIn: 'root',
})
export class CmsFacilitiesService {
  public facilitiesContent$!: Observable<FacilitiesResponse>;

  private cmsContent = inject(CmsContentService);

  private activeRequests = new BehaviorSubject(0);

  private data = new Subject<Facility>();
  selectedFacility = this.data.asObservable();

  private store = createStore(
    { name: 'cmsFacilitiesContent' },
    withProps<{
      content: FacilitiesResponse;
    }>({
      content: [],
    })
  );

  public fetch(stateCode?: string): Observable<void> {
    this.activeRequests.next(this.activeRequests.value + 1);

    return this.cmsContent.getFacilitiesContent(stateCode).pipe(
      tap((content) => this.setFacilitiesContent(content)),
      mapTo(void 0),
      finalize(() => this.activeRequests.next(this.activeRequests.value - 1))
    );
  }

  updateSelectedFacility(facility: Facility) {
    this.data.next(facility);
  }

  public getFacilityByStateAndName(
    stateCode: string,
    facilityName: string
  ): Observable<Facility | undefined> {
    return this.fetch(stateCode).pipe(
      map(() => {
        const facilities = this.store.value.content;
        return facilities.find(
          (facility: Facility) =>
            facility.state.trim() === stateCode &&
            facility.facility_name
              .replace(/\./g, '')
              .replace(/\s/g, '-')
              .replace(/--+/g, '-')
              .toLowerCase() === facilityName
        );
      })
    );
  }

  public getFacilitiesContent(
    stateCode?: string
  ): Observable<FacilitiesResponse> {
    this.facilitiesContent$ = this.store.pipe(select((state) => state.content));
    this.fetch(stateCode).subscribe();
    return this.facilitiesContent$;
  }

  private setFacilitiesContent(content: FacilitiesResponse): void {
    this.store.update(setProp('content', content));
  }
}
